import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useTranslation } from '../../hooks/useTranslation';

const Checklist = ({ onComplete, isPhase2Compliant }) => {
  const { t, language } = useTranslation();
  const isRtl = language === 'ar';

  const [checkedItems, setCheckedItems] = useState({
    fatoora: false,
    vat: false,
    documents: false,
    contact: false
  });

  const checklistItems = [
    {
      id: 'fatoora',
      title: t('ZatcaOnboarding.Checklist.Items.Fatoora_Title'),
      description: t('ZatcaOnboarding.Checklist.Items.Fatoora_Desc'),
    },
    {
      id: 'vat',
      title: t('ZatcaOnboarding.Checklist.Items.Vat_Title'),
      description: t('ZatcaOnboarding.Checklist.Items.Vat_Desc'),
    },
    {
      id: 'documents',
      title: t('ZatcaOnboarding.Checklist.Items.Documents_Title'),
      description: t('ZatcaOnboarding.Checklist.Items.Documents_Desc'),
    },
    {
      id: 'contact',
      title: t('ZatcaOnboarding.Checklist.Items.Contact_Title'),
      description: t('ZatcaOnboarding.Checklist.Items.Contact_Desc'),
    }
  ];

  const handleCheckboxChange = (id) => (event) => {
    setCheckedItems(prev => ({
      ...prev,
      [id]: event.target.checked
    }));
  };

  const isAllChecked = Object.values(checkedItems).every(value => value);

  return (
    <Box dir={isRtl ? 'rtl' : 'ltr'} sx={{ direction: isRtl ? 'rtl' : 'ltr' }}>
      <Card 
        variant="outlined" 
        sx={{ 
          mb: 4,
          borderRadius: 0,
          borderColor: 'divider',
          width: '100%',
          minHeight: 420
        }}
      >
        <CardContent>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              mb: 2,
              fontWeight: 500,
              color: 'text.secondary',
              fontSize: '0.875rem',
              textAlign: isRtl ? 'right' : 'left'
            }}
          >
            {t('ZatcaOnboarding.Checklist.Title')}
          </Typography>

          <Stack spacing={1}>
            {checklistItems.map((item) => (
              <Box
                key={item.id}
                sx={{
                  p: 1.5,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 0,
                  bgcolor: 'background.paper'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={checkedItems[item.id]}
                      onChange={handleCheckboxChange(item.id)}
                      sx={{ 
                        '&.Mui-checked': {
                          color: 'primary.main',
                        },
                        py: 0,
                        ...(isRtl ? { ml: 1, mr: 0 } : { mr: 1, ml: 0 })
                      }}
                    />
                  }
                  label={
                    <Box sx={{ width: '100%', textAlign: isRtl ? 'right' : 'left' }}>
                      <Typography 
                        variant="subtitle1" 
                        sx={{ 
                          fontWeight: 500,
                          color: 'text.primary',
                          lineHeight: 1.2,
                          mb: 0.5,
                          textAlign: isRtl ? 'right' : 'left'
                        }}
                      >
                        {item.title}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: 'text.secondary',
                          lineHeight: 1.4,
                          textAlign: isRtl ? 'right' : 'left'
                        }}
                      >
                        {item.description}
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    alignItems: 'flex-start',
                    margin: 0,
                    width: '100%',
                    '& .MuiFormControlLabel-label': {
                      flex: 1
                    }
                  }}
                />
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end' }}>
        <Button
          variant="contained"
          onClick={onComplete}
          disabled={!isAllChecked || !isPhase2Compliant}
          sx={{ 
            px: 3,
            py: 1,
            borderRadius: 0,
            textTransform: 'none',
            fontWeight: 500,
            fontSize: '0.875rem',
            width: { xs: '100%', sm: 'auto' }
          }}
        >
          {t('ZatcaOnboarding.Checklist.Complete_Button')}
        </Button>
      </Box>
    </Box>
  );
};

export default Checklist;